import { Accordion, AccordionDetails, Box, Button } from "@mui/material";
import { makeStyles } from "tss-react/mui";
import { ArrowRightIcon, CalendarIcon, CsvExportIcon, DeleteIcon, SaveIcon } from "../Primitives/Icons";
import { FC, useState } from "react";
import { useTranslation } from "react-i18next";
import { Colors } from "../../style/Colors";
import { useButtonStyles } from "../../style/buttons.style";
import { IPomReportingListItem } from "../../domain/reporting/pom-reporting";
import { IPomReportingTableData } from "../reporting-table/pom-reporting-table-data";
import { PomReportingStatus } from "../../repositories/models/reportings/pom-reporting-status";
import { PomReportingSubmitDialog } from "../reporting-dialog/pom-reporting-submit-dialog.component";
import { PomReportingPeriodPicker } from "../reporting-period-picker/pom-reporting-period-picker.component";
import { QuestionDialog } from "../Dialogs/question-dialog";
import { getReportingPeriodLabel } from "../../utils/reporting-period-label.util";

const useStyles = makeStyles()({
  container: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    flexWrap: "wrap",
    gap: "8px",
    padding: "16px 0",
    marginBottom: "10px",
  },
  infoContainer: {
    display: "flex",
    alignItems: "center",
    flexWrap: "nowrap",
    gap: "12px",
    "@media (max-width: 1200px)": {
      gap: "8px",
    },
    "@media (max-width: 900px)": {
      gap: "4px",
    },
  },
  chipOpen: {
    backgroundColor: Colors.orange,
    color: Colors.white,
  },
  chipReported: {
    backgroundColor: Colors.greenLight,
    color: Colors.white,
  },
  chipCorrected: {
    backgroundColor: Colors.purple,
    color: Colors.white,
  },
  changePeriodButton: {
    minHeight: "32px",
    textTransform: "none",
  },
  actionButton: {
    minHeight: "32px",
  },
  periodAccordion: {
    boxShadow: "none",
    "&::before": {
      display: "none",
    },
    "&.Mui-expanded": {
      margin: 0,
    },
  },
  periodAccordionDetails: {
    padding: 0,
    position: "absolute",
    zIndex: 10,
    left: 0,
  },
});

interface IPomReportingHeaderProps {
  reportingList: IPomReportingListItem[];
  currentReportingNo: number | undefined;
  positions: IPomReportingTableData[];
  isDirty: boolean;
  isLoading: boolean;
  hasDraft: boolean;
  isSavingDraft: boolean;
  draftReportingNos: number[];
  hasUnsavedDraftChanges: boolean;
  onSubmit: () => Promise<boolean>;
  onReportingSelected: (reportingNo: number) => void;
  onSaveDraft: () => Promise<void>;
  onDiscardDraft: () => Promise<void>;
  onOpenImportDialog: () => void;
  onOpenExportDialog: () => void;
}

export const PomReportingHeader: FC<IPomReportingHeaderProps> = (props) => {
  const {
    reportingList,
    currentReportingNo,
    positions,
    isDirty,
    isLoading,
    hasDraft,
    isSavingDraft,
    draftReportingNos,
    hasUnsavedDraftChanges,
    onSubmit,
    onReportingSelected,
    onSaveDraft,
    onDiscardDraft,
    onOpenImportDialog,
    onOpenExportDialog,
  } = props;
  const { classes } = useStyles();
  const { classes: buttonClasses } = useButtonStyles();
  const { t } = useTranslation();
  const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false);
  const [isPeriodPickerOpen, setIsPeriodPickerOpen] = useState<boolean>(false);
  const [isDiscardDialogOpen, setIsDiscardDialogOpen] = useState<boolean>(false);

  const currentReporting = reportingList.find((r) => r.reportingNo === currentReportingNo);

  const getStatusColor = (status: PomReportingStatus): string => {
    switch (status) {
      case PomReportingStatus.Reported:
        return Colors.greenLight;
      case PomReportingStatus.Corrected:
        return Colors.purple;
      default:
        return Colors.orange;
    }
  };

  const handleSubmitClick = (): void => {
    setIsDialogOpen(true);
  };

  const handleDialogConfirm = async (): Promise<void> => {
    const success = await onSubmit();
    if (success) {
      setIsDialogOpen(false);
    }
  };

  const handleDialogCancel = (): void => {
    setIsDialogOpen(false);
  };

  const handleReportingSelected = (reportingNo: number): void => {
    onReportingSelected(reportingNo);
    setIsPeriodPickerOpen(false);
  };

  return (
    <>
      <Box className={classes.container}>
        <Box className={classes.infoContainer}>
          <Box style={{ position: "relative" }}>
            <Accordion
              expanded={isPeriodPickerOpen}
              onChange={() => setIsPeriodPickerOpen((prev) => !prev)}
              className={classes.periodAccordion}
            >
              <Button
                variant="contained"
                color="primary"
                className={classes.changePeriodButton}
                endIcon={<CalendarIcon svgColor="white" />}
                onClick={() => setIsPeriodPickerOpen((prev) => !prev)}
              >
                {t("pom.reportings.header.change_period")}
              </Button>
              <AccordionDetails className={classes.periodAccordionDetails}>
                <PomReportingPeriodPicker
                  reportingList={reportingList}
                  currentReportingNo={currentReportingNo}
                  isLoading={isLoading}
                  draftReportingNos={draftReportingNos}
                  onReportingSelected={handleReportingSelected}
                />
              </AccordionDetails>
            </Accordion>
          </Box>
          {currentReporting && (
            <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                height: "28px",
                px: 1.5,
                borderRadius: "14px",
                border: `2px solid ${getStatusColor(currentReporting.status)}`,
                background: `repeating-linear-gradient(
                  -45deg,
                  ${getStatusColor(currentReporting.status)}18,
                  ${getStatusColor(currentReporting.status)}18 2px,
                  transparent 2px,
                  transparent 8px
                )`,
                color: getStatusColor(currentReporting.status),
                fontWeight: 700,
                fontSize: "0.8125rem",
                letterSpacing: "0.02em",
                userSelect: "none",
                pointerEvents: "none",
              }}
            >
              {t(`pom.reportings.status.${currentReporting.status}`)}
            </Box>
          )}
          {hasDraft && (
            <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                height: "28px",
                px: 1.5,
                borderRadius: "14px",
                border: `2px solid ${Colors.orange}`,
                background: `repeating-linear-gradient(
                  -45deg,
                  ${Colors.orange}18,
                  ${Colors.orange}18 2px,
                  transparent 2px,
                  transparent 8px
                )`,
                color: Colors.orange,
                fontWeight: 700,
                fontSize: "0.8125rem",
                letterSpacing: "0.02em",
                userSelect: "none",
                pointerEvents: "none",
              }}
            >
              {t("pom.reportings.status.draft")}
            </Box>
          )}
        </Box>
        {currentReporting && (
          <Box style={{ display: "flex", gap: "8px", alignItems: "center", flexWrap: "wrap" }}>
            <Box style={{ display: "flex", gap: "8px", alignItems: "center", flexWrap: "nowrap" }}>
              {hasDraft && (
                <Button
                  variant="contained"
                  color="secondary"
                  disabled={isLoading}
                  onClick={() => setIsDiscardDialogOpen(true)}
                  endIcon={<DeleteIcon svgColor={isLoading ? "black" : "white"} />}
                  className={`${buttonClasses.danger} ${classes.actionButton}`}
                >
                  {t("pom.reportings.actions.discardDraft")}
                </Button>
              )}
              <Button
                variant="outlined"
                disabled={!hasUnsavedDraftChanges || isLoading || isSavingDraft}
                onClick={() => void onSaveDraft()}
                endIcon={<SaveIcon svgColor={!hasUnsavedDraftChanges || isLoading || isSavingDraft ? "white" : "green"} />}
                className={classes.actionButton}
              >
                {t("pom.reportings.actions.saveDraft")}
              </Button>
            </Box>
            <Box style={{ display: "flex", gap: "8px", alignItems: "center", flexWrap: "nowrap" }}>
              <Button
                variant="contained"
                color="primary"
                disabled={isLoading}
                onClick={onOpenImportDialog}
                endIcon={<CsvExportIcon />}
                className={classes.actionButton}
              >
                {t("pom.reportings.actions.importExcel")}
              </Button>
              <Button
                variant="contained"
                color="primary"
                disabled={isLoading}
                onClick={onOpenExportDialog}
                endIcon={<SaveIcon svgColor="white" />}
                className={classes.actionButton}
              >
                {t("pom.reportings.actions.exportExcel")}
              </Button>
              <Button
                variant="contained"
                color="primary"
                disabled={!isDirty}
                onClick={handleSubmitClick}
                endIcon={<ArrowRightIcon svgColor="white" />}
                className={classes.actionButton}
              >
                {t("pom.reportings.header.submit")}
              </Button>
            </Box>
          </Box>
        )}
      </Box>
      <PomReportingSubmitDialog
        open={isDialogOpen}
        positions={positions}
        reportingPeriodLabel={
          currentReporting
            ? getReportingPeriodLabel(currentReporting.reportingPeriodStartDate, currentReporting.reportingPeriodEndDate)
            : ""
        }
        onConfirm={handleDialogConfirm}
        onCancel={handleDialogCancel}
      />
      <QuestionDialog
        isOpen={isDiscardDialogOpen}
        title={t("pom.reportings.draft.discardConfirmTitle")}
        message={t("pom.reportings.draft.discardConfirmMessage")}
        onAccept={async () => {
          setIsDiscardDialogOpen(false);
          await onDiscardDraft();
        }}
        onClose={() => setIsDiscardDialogOpen(false)}
        displayStyle="warning"
      />
    </>
  );
};
